/* eslint-disable no-unused-vars */
import { DateTime } from 'luxon';

import fr from './fr';
import de from './de';
import en from './en';
import type { Translations } from './translationsTypes';
import { DEFAULT_LOCALE, type Locale } from '@/shared/constants/locales';

export enum Formats {
    ADF_TIME,
    ADF_TIME_UTC,
    ADF_TIME_WITH_SECONDS,
    ADF_DATE,
    ADF_DATE_WITHOUT_YEAR,
    ADF_DATE_TRUNC,
    ADF_DATE_TIMEZONE,
    ADF_DATE_SHORT,
    ADF_DATE_SHORT_INTERNATIONAL,
    ADF_DATE_GLOBAL,
    ADF_TIME_SPECIAL,
    ADF_TIME_DATE_WITHOUT_YEAR,
    ADF_DATESHORT_TIME,
    ADF_DATE_TIME_FULL,
    ADF_TIME_DATE_FULL,
    ADF_DATE_TIME,
    ADF_TIME_DATE,
    ADF_DATE_SHORT_TIME_LOCALIZED,
    ADF_DATE_SHORT_TIME_UNFORMATED,
    ADF_DATE_TIME_PLAIN,
}

export const FormatsLanguage = {
    fr: {
        ADF_TIME: 'HH:mm',
        ADF_TIME_UTC: 'HH:mm:ss UTC',
        ADF_TIME_WITH_SECONDS: 'HH:mm:ss',
        ADF_DATE: 'JJ MMMM AAAA',
        ADF_DATE_WITHOUT_YEAR: 'JJ MMMM',
        ADF_DATE_TRUNC: 'JJ MMM AAAA',
        ADF_DATE_TIMEZONE: 'JJ MMMM AAAA à HH:mm:ss ZZZZ',
        ADF_DATE_SHORT: 'JJ-MM-AAAA',
        ADF_DATE_SHORT_INTERNATIONAL: 'MM-JJ-AAAA',
        ADF_DATE_GLOBAL: 'JJ-MM-AAAA',
        ADF_TIME_SPECIAL: '{HH}h{mm}',
        ADF_TIME_DATE_WITHOUT_YEAR: 'HH:mm le JJ MMMM',
        ADF_DATESHORT_TIME: 'JJ-MM-AAAA à HH:mm',
        ADF_DATE_TIME_FULL: 'le JJ MMMM AAAA à HH:mm:ss',
        ADF_TIME_DATE_FULL: 'à HH:mm:ss le JJ MM AAAA',
        ADF_DATE_TIME: 'le JJ MMMM AAAA à HH:mm',
        ADF_TIME_DATE: 'à HH:mm le JJ MMMM',
        ADF_DATE_SHORT_TIME_LOCALIZED: 'JJ-MM-AAAA {HH}:{mm}',
        ADF_DATE_SHORT_TIME_UNFORMATED: 'MM-JJ-AAAA {HH}:{mm}',
        ADF_DATE_TIME_PLAIN: 'JJ MMMM AAAA à HH:mm',
    },
    de: {
        ADF_TIME: 'HH:mm',
        ADF_TIME_UTC: 'HH:mm:ss MEZ',
        ADF_TIME_WITH_SECONDS: 'HH:mm:ss',
        ADF_DATE: 'TT MMMM JJJJ',
        ADF_DATE_WITHOUT_YEAR: 'TT MMMM',
        ADF_DATE_TRUNC: 'TT MMM JJJJ',
        ADF_DATE_TIMEZONE: 'TT MMMM JJJJ um HH:mm:ss MEZ',
        ADF_DATE_SHORT: 'TT.MM.JJJJ',
        ADF_DATE_SHORT_INTERNATIONAL: 'TT.MM.JJJJ',
        ADF_DATE_GLOBAL: 'TT-MM-JJJJ',
        ADF_TIME_SPECIAL: '{HH}:{mm} Uhr',
        ADF_TIME_DATE_WITHOUT_YEAR: 'HH:mm um TT MMMM',
        ADF_DATESHORT_TIME: 'TT-MM-JJJJ um HH:mm',
        ADF_DATE_TIME_FULL: 'um TT MMMM JJJJ um HH:mm:ss',
        ADF_TIME_DATE_FULL: 'HH:mm:ss um TT MM JJJJ',
        ADF_DATE_TIME: 'um TT MMMM JJJJ um HH:mm',
        ADF_TIME_DATE: 'HH:mm um TT MMMM',
        ADF_DATE_SHORT_TIME_LOCALIZED: 'TT.MM.JJJJ {HH}:{mm}',
        ADF_DATE_SHORT_TIME_UNFORMATED: 'MM.TT.JJJJ {HH}:{mm}',
        ADF_DATE_TIME_PLAIN: 'TT. MMMM JJJJ um HH:mm',
    },
    en: {
        ADF_TIME: 'hh:mm a',
        ADF_TIME_UTC: 'hh:mm:ss a ZZZZ',
        ADF_TIME_WITH_SECONDS: 'hh:mm:ss a',
        ADF_DATE: 'MMMM DD, YYYY',
        ADF_DATE_WITHOUT_YEAR: 'MMMM DD',
        ADF_DATE_TRUNC: 'MMM DD, YYYY',
        ADF_DATE_TIMEZONE: 'MMMM DD, YYYY at hh:mm:ss a ZZZZ',
        ADF_DATE_SHORT: 'MM-DD-YYYY',
        ADF_DATE_SHORT_INTERNATIONAL: 'MM-DD-YYYY',
        ADF_DATE_GLOBAL: 'DD-MM-YYYY',
        ADF_TIME_SPECIAL: '{HH}:{MM} a',
        ADF_TIME_DATE_WITHOUT_YEAR: 'hh:mm a on MMMM DD',
        ADF_DATESHORT_TIME: 'MM-DD-YYYY at hh:mm',
        ADF_DATE_TIME_FULL: 'on MMMM DD, YYYY at hh:mm:ss a',
        ADF_TIME_DATE_FULL: 'hh:mm:ss a on MM DD YYYY',
        ADF_DATE_TIME: 'on MMMM DD, YYYY at hh:mm a',
        ADF_TIME_DATE: 'at hh:mm a on MMMM DD, YYYY',
        ADF_DATE_SHORT_TIME_LOCALIZED: 'MM-DD-YYYY {HH}:{mm}',
        ADF_DATE_SHORT_TIME_UNFORMATED: 'MM-DD-YYYY {HH}:{mm}',
        ADF_DATE_TIME_PLAIN: 'MMMM D, YYYY at hh:mm a',
    },
} as const;

const translationsMap: Record<string, Translations> = {
    fr,
    de,
    en,
};

const internalLang = translationsMap['en']!;


export function formatDate(dateString: string, format: Formats, locale: Locale = DEFAULT_LOCALE): string {
    const lang = translationsMap[locale]!;
    const date = DateTime.fromISO(dateString, {
        locale
    });

    const formatMap: Record<Formats, () => string> = {
        [Formats.ADF_TIME]: () => date.toFormat(lang.HourMin),
        [Formats.ADF_TIME_UTC]: () => date.toFormat(lang.HourMinFull),
        [Formats.ADF_TIME_WITH_SECONDS]: () => date.toFormat(lang.HourMinSecond),
        [Formats.ADF_DATE]: () => date.toFormat(lang.Date),
        [Formats.ADF_DATE_TRUNC]: () => date.toFormat(lang.Trunc),
        [Formats.ADF_DATE_SHORT]: () => date.toFormat(lang.Classic),
        [Formats.ADF_DATE_SHORT_INTERNATIONAL]: () => date.toFormat(internalLang.Classic),
        [Formats.ADF_DATE_GLOBAL]: () => date.toFormat(lang.DateGlobal),
        [Formats.ADF_DATE_TIMEZONE]: () => formatDateTimeWithTimezone(date, lang),
        [Formats.ADF_DATE_WITHOUT_YEAR]: () => formatDayMonthWithoutYear(date, lang),
        [Formats.ADF_TIME_SPECIAL]: () => formatTimeSpecial(date, lang),
        [Formats.ADF_TIME_DATE_WITHOUT_YEAR]: () => formatTimeDateWithoutYear(date, lang),
        [Formats.ADF_DATESHORT_TIME]: () => formatDateShortTime(date, lang),
        [Formats.ADF_DATE_TIME_FULL]: () => formatDateTimeFull(date, lang),
        [Formats.ADF_TIME_DATE_FULL]: () => formatTimeDateFull(date, lang),
        [Formats.ADF_DATE_TIME]: () => formatDateTime(date, lang),
        [Formats.ADF_TIME_DATE]: () => formatTimeDate(date, lang),
        [Formats.ADF_DATE_SHORT_TIME_LOCALIZED]: () => formatDateShortTimeLocalized(date, dateString, lang),
        [Formats.ADF_DATE_SHORT_TIME_UNFORMATED]: () => formatDateShortTimeUnformatted(date, dateString),
        [Formats.ADF_DATE_TIME_PLAIN]: () => formatDateTimePlain(date, lang),
    };

    const formatFunction = formatMap[format];

    if (formatFunction) {
        return formatFunction();
    } else {
        throw new Error(`Format ${format} not defined.`);
    }
}

function formatDateTimeWithTimezone(date: DateTime, lang: Translations): string {
    const timeFormat = date.toFormat(lang.HourMinFull);
    const formatDate = date.toFormat(lang.Date);
    return lang.AdfDate.replace('{TIME}', timeFormat).replace('{DATE}', formatDate);
}

function formatDayMonthWithoutYear(date: DateTime, lang: Translations): string {
    const dayFormat = date.toFormat(lang.Day);
    const monthFormat = date.toFormat(lang.Month);
    return lang.AdfDayMonth.replace('{DAY}', dayFormat).replace('{MONTH}', monthFormat);
}

function formatTimeSpecial(date: DateTime, lang: Translations): string {
    const hourFormat = date.toFormat(lang.Hour);
    const minFormat = date.toFormat(lang.Min);
    const extendFormat = date.toFormat(lang.Extend);
    return lang.TimeSpecial.replace('{HOUR}', hourFormat)
        .replace('{MIN}', minFormat)
        .replace('{EXTEND}', extendFormat);
}

function formatTimeDateWithoutYear(date: DateTime, lang: Translations): string {
    const timeFormat = date.toFormat(lang.HourMin);
    const formatDate = `${date.day} ${date.toFormat(lang.Month)}`;
    return lang.AdfTime.replace('{TIME}', timeFormat).replace('{DATE}', formatDate);
}

function formatDateShortTime(date: DateTime, lang: Translations): string {
    const timeFormat = date.toFormat(lang.HourMin);
    const formatDate = date.toFormat(lang.Classic);
    return lang.AdfDate.replace('{TIME}', timeFormat).replace('{DATE}', formatDate);
}

function formatDateTimeFull(date: DateTime, lang: Translations): string {
    const timeFormat = date.toFormat(lang.HourMinSecond);
    const formatDate = date.toFormat(lang.Date);
    return lang.AdfDateTime.replace('{TIME}', timeFormat).replace('{DATE}', formatDate);
}

function formatTimeDateFull(date: DateTime, lang: Translations): string {
    const timeFormat = date.toFormat(lang.HourMinSecond);
    const formatDate = date.toFormat(lang.Date);
    return lang.AdfTimeDate.replace('{TIME}', timeFormat).replace('{DATE}', formatDate);
}

function formatDateTime(date: DateTime, lang: Translations): string {
    const timeFormat = date.toFormat(lang.HourMin);
    const formatDate = date.toFormat(lang.Date);
    return lang.AdfDateTime.replace('{TIME}', timeFormat).replace('{DATE}', formatDate);
}

function formatTimeDate(date: DateTime, lang: Translations): string {
    const timeFormat = date.toFormat(lang.HourMin);
    const formatDate = date.toFormat(lang.Date);
    return lang.AdfTimeDate.replace('{TIME}', timeFormat).replace('{DATE}', formatDate);
}

function formatDateShortTimeLocalized(date: DateTime, dateString: string, lang: Translations): string {
    const timeValue = formatDate(dateString, Formats.ADF_TIME, 'fr');
    const dateValue = date.toFormat(lang.Classic);
    return `${dateValue} ${timeValue}`;
}

function formatDateTimePlain(date: DateTime, lang: Translations): string {
    const timeFormat = date.toFormat(lang.HourMin);
    const formatDate = date.toFormat(lang.Date);
    return lang.AdfDate.replace('{DATE}', formatDate).replace('{TIME}', timeFormat);
}

function formatDateShortTimeUnformatted(date: DateTime, dateString: string): string {
    const timeValue = formatDate(dateString, Formats.ADF_TIME, 'fr');
    const dateValue = date.toFormat(internalLang.Classic);
    return `${dateValue} ${timeValue}`;
}
